<template>
    <div class="container-fluid pt-3">
        <loader v-if="loading"></loader>

        <KvCard v-if="!loading && product" class="product-hero-card">
            <template #text>
                <div class="row">
                    <div class="col-lg-5 col-md-6 mb-4 mb-md-0">
                        <div class="gallery">
                            <div class="gallery-main mb-3" v-if="images.length > 0">
                                <img :src="selectedImage" class="gallery-main-img" alt="" />
                            </div>
                            <div class="gallery-main mb-3" v-else>
                                <div class="gallery-placeholder">
                                    <span class="fa fa-image"></span>
                                    <p class="text-muted mt-2 mb-0">{{ t('settings-products-no_images') }}</p>
                                </div>
                            </div>
                            <div class="gallery-thumbs" v-if="images.length > 1">
                                <img
                                    v-for="(img, index) in images"
                                    :key="index"
                                    :src="img"
                                    :class="['gallery-thumb', { active: selectedImage === img }]"
                                    @click="selectedImage = img"
                                    alt=""
                                />
                            </div>
                        </div>
                    </div>

                    <div class="col-lg-7 col-md-6">
                        <div v-if="categoryBadges.length > 0" class="product-badges mb-2">
                            <span
                                v-for="(badge, idx) in categoryBadges"
                                :key="`${idx}-${badge}`"
                                class="badge product-category-badge"
                            >
                                {{ badge }}
                            </span>
                        </div>

                        <div class="d-flex align-items-start justify-content-between mb-2">
                            <h2 class="mb-0 product-title">{{ product.name }}</h2>
                            <span class="badge ml-2" :class="statusBadgeClass(product.status)" style="font-size: 0.85rem">
                                {{ statusLabel(product.status) }}
                            </span>
                        </div>

                        <p class="text-muted mb-3 product-description" v-if="product.description">{{ product.description }}</p>

                        <div class="product-info-grid">
                            <div class="info-item" v-if="product.cost"><label>{{ t('settings-products-cost') }}</label><span>{{ currencySymbol }} {{ formatPrice(product.cost) }}</span></div>
                            <div class="info-item" v-if="product.brand"><label>{{ t('settings-products-brand') }}</label><span>{{ product.brand }}</span></div>
                            <div class="info-item" v-if="product.stock_quantity !== null && product.stock_quantity !== undefined"><label>{{ t('settings-products-stock') }}</label><span>{{ product.stock_quantity }}</span></div>
                            <div class="info-item" v-if="product.product_external_id"><label>{{ t('settings-products-external_id') }}</label><span class="text-muted">{{ product.product_external_id }}</span></div>
                        </div>
                    </div>
                </div>
            </template>
        </KvCard>

        <div v-if="!loading && product" class="stats-insights mt-3 mb-3">
            <KvCard density="compact" variant="soft" class="stat-insight stat-insight-white">
                <template #title><div class="stat-title">{{ t('settings-products-orders_12m') }}</div></template>
                <template #text><div class="stat-value">{{ product.recent_ordered_count || 0 }}</div></template>
            </KvCard>
            <KvCard density="compact" variant="soft" class="stat-insight stat-insight-white">
                <template #title><div class="stat-title">{{ t('settings-products-revenue_12m') }}</div></template>
                <template #text><div class="stat-value">{{ currencySymbol }} {{ formatPrice(product.recent_total_revenue || 0) }}</div></template>
            </KvCard>
            <KvCard density="compact" variant="soft" class="stat-insight stat-insight-white">
                <template #title><div class="stat-title">{{ t('settings-products-current_price') }}</div></template>
                <template #text><div class="stat-value">{{ currencySymbol }} {{ formatPrice(product.price || 0) }}</div></template>
            </KvCard>
            <KvCard density="compact" variant="soft" class="stat-insight stat-insight-white">
                <template #title><div class="stat-title">{{ t('settings-products-stock') }}</div></template>
                <template #text><div class="stat-value">{{ product.stock_quantity !== null && product.stock_quantity !== undefined ? product.stock_quantity : '—' }}</div></template>
            </KvCard>
            <KvCard density="compact" variant="soft" class="stat-insight stat-insight-white">
                <template #title><div class="stat-title">{{ t('settings-products-last_purchase') }}</div></template>
                <template #text><div class="stat-value">{{ product.last_purchased_at ? formatDate(product.last_purchased_at) : '—' }}</div></template>
            </KvCard>
        </div>

        <KvCard v-if="!loading && product" class="product-content-card mt-3">
            <template #text>
                <KvTabs v-model="activeTab" grow>
                    <template #tabs>
                        <KvTab :value="'purchases'">{{ t('settings-products-purchase_history') }}</KvTab>
                        <KvTab :value="'copurchase'">{{ t('settings-products-bought_together') }}</KvTab>
                        <KvTab :value="'also_bought'">{{ t('settings-products-also_bought') }}</KvTab>
                        <KvTab :value="'ai'">{{ t('settings-products-ai') }}</KvTab>
                        <KvTab :value="'manual'">{{ t('settings-products-manual') }}</KvTab>
                    </template>
                    <template #panels>

                        <KvTabPanel value="purchases">
                            <p class="text-muted small mb-3">{{ t('settings-products-last_10_orders_including_product') }}</p>
                            <grid :source="purchaseHistorySource" :quantityPerPage="10" :show-search="false" :display-items-per-page="false" name="productPurchaseHistory">
                                <template v-slot:rows="grid">
                                    <thead>
                                        <tr>
                                            <th>{{ t('date') }}</th>
                                            <th>{{ t('client') }}</th>
                                            <th>{{ t('quantity_short') }}</th>
                                            <th>{{ t('settings-products-price_unit') }}</th>
                                            <th>{{ t('total') }}</th>
                                            <th>{{ t('status') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="p in grid.data" :key="p.order_id">
                                            <td>{{ formatDate(p.created) }}</td>
                                            <td>
                                                <router-link :to="'/client/' + p.client_id" class="text-decoration-none">
                                                    {{ p.first_name }} {{ p.last_name }}
                                                </router-link>
                                            </td>
                                            <td>{{ p.quantity }}</td>
                                            <td>{{ currencySymbol }} {{ formatPrice(p.price) }}</td>
                                            <td>{{ currencySymbol }} {{ formatPrice(p.total_price) }}</td>
                                            <td>
                                                <span class="badge" :class="p.status === 'approved' ? 'badge-success' : 'badge-secondary'">
                                                    {{ p.status }}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </grid>
                        </KvTabPanel>

                        <KvTabPanel value="copurchase">
                            <p class="text-muted small mb-3" v-html="t('settings-products-bought_in_same_order')"></p>
                            <div v-if="recsLoading" class="text-center py-3"><span class="fa fa-spinner fa-spin"></span></div>
                            <div v-else>
                                <recommendation-list :items="recommendations.copurchase" />
                            </div>
                        </KvTabPanel>

                        <KvTabPanel value="also_bought">
                            <p class="text-muted small mb-3" v-html="t('settings-products-clients_who_bought_also_bought')"></p>
                            <div v-if="recsLoading" class="text-center py-3"><span class="fa fa-spinner fa-spin"></span></div>
                            <div v-else>
                                <recommendation-list :items="recommendations.also_bought" />
                            </div>
                        </KvTabPanel>

                        <KvTabPanel value="ai">
                            <p class="text-muted small mb-3" v-html="t('settings-products-ai_recommendation_disclaimer')"></p>
                            <div class="kv-field kv-field-ai mb-3">
                                <input
                                    type="text"
                                    class="form-control kv-field-input"
                                    v-model="aiPrompt"
                                    :placeholder="t('settings-products-ai_placeholder')"
                                    @keydown.enter="loadAiRecs"
                                />
                                <KvButton
                                    class="kv-field-btn"
                                    size="regular"
                                    variant="ai"
                                    @click="loadAiRecs"
                                    :loading="aiLoading"
                                    :disabled="aiLoading"
                                >
                                    {{ t('settings-products-use_ai') }}
                                </KvButton>
                            </div>

                            <div v-if="aiLoading" class="ai-loading">
                                <KvSkeleton class="mb-2" :rows="1" :width="['45%']" height="12px" />
                                <div class="rec-list">
                                    <div class="rec-item" v-for="i in 5" :key="`ai-sk-${i}`">
                                        <div class="d-flex align-items-center" style="gap: 10px">
                                            <div class="ai-thumb-skeleton" />
                                            <div class="flex-1">
                                                <KvSkeleton :rows="2" :width="['70%','45%']" height="10px" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else>
                                <recommendation-list :items="recommendations.ai" />
                            </div>
                        </KvTabPanel>

                        <KvTabPanel value="manual">
                            <p class="text-muted small mb-3">{{ t('settings-products-manual_recommendation_disclaimer') }}</p>

                            <div class="kv-field kv-field-manual mb-3">
                                <input
                                    type="text"
                                    class="form-control kv-field-input"
                                    :placeholder="t('settings-products-search_product_to_link')"
                                    v-model="linkSearch"
                                    @keydown.enter="searchProductsToLink"
                                />
                                <KvButton
                                    class="kv-field-btn"
                                    size="regular"
                                    variant="outlined"
                                    @click="searchProductsToLink"
                                    :loading="linkLoading"
                                    :disabled="!linkSearch || linkLoading"
                                >
                                    {{ t('search') }}
                                </KvButton>
                            </div>

                            <div v-if="linkResults.length > 0" class="link-results mb-3">
                                <div v-for="p in linkResults" :key="p.id" class="link-result-item">
                                    <div class="d-flex align-items-center justify-content-between">
                                        <div class="d-flex align-items-center" style="gap: 8px; min-width: 0">
                                            <img v-if="firstImage(p)" :src="firstImage(p)" class="link-thumb" alt="" />
                                            <span class="fa fa-image link-thumb-placeholder" v-else></span>
                                            <span class="small text-truncate" style="max-width: 100%">{{ p.name }}</span>
                                        </div>
                                        <KvButton size="small" variant="outlined" @click="addRelated(p.id)" :loading="saving" :disabled="saving">
                                            {{ t('add') }}
                                        </KvButton>
                                    </div>
                                </div>
                            </div>

                            <div class="manual-added">
                                <div class="d-flex align-items-center justify-content-between mb-2">
                                    <div class="fw-500" style="font-size: 0.95rem">{{ t('settings-products-added_products') }}</div>
                                    <div v-if="relatedLoading" class="text-muted small"><span class="fa fa-spinner fa-spin"></span> {{ t('loading') }}</div>
                                </div>

                                <div v-if="!relatedLoading && relatedProducts.length === 0" class="text-muted small">
                                    {{ t('settings-products-no_manual_links') }}
                                </div>

                                <div v-else class="rec-list">
                                    <div v-for="item in relatedProducts" :key="item.related_product_id || item.id" class="rec-item">
                                        <div class="rec-row">
                                            <router-link :to="'/settings/products/' + (item.related_product_id || item.id)" class="rec-photo" style="text-decoration: none; color: inherit">
                                                <img v-if="firstImage(item)" :src="firstImage(item)" class="rec-thumb" alt="" />
                                                <span v-else class="fa fa-image rec-thumb-placeholder"></span>
                                            </router-link>
                                            <div class="rec-info-col">
                                                <div class="rec-info-top">
                                                    <div class="rec-info-main">
                                                        <div class="rec-title">{{ item.name }}</div>
                                                        <span v-if="item.price" class="badge badge-light rec-price-badge">{{ currencySymbol }} {{ formatPrice(item.price) }}</span>
                                                    </div>
                                                    <div class="d-flex align-items-center" style="gap: 6px">
                                                        <KvButton size="small" variant="outlined" class="rec-remove-btn" @click="removeRelated(item.related_product_id || item.id)" :loading="saving" :disabled="saving">
                                                            {{ t('remove') }}
                                                        </KvButton>
                                                    </div>
                                                </div>
                                                <input
                                                    type="text"
                                                    class="form-control form-control-sm rec-justification-input"
                                                    :placeholder="t('settings-products-add_recommendation_optional')"
                                                    v-model="manualReasonDrafts[item.related_product_id || item.id]"
                                                    @input="scheduleManualReasonSave(item.related_product_id || item.id)"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </KvTabPanel>

                    </template>
                </KvTabs>
            </template>
        </KvCard>

        <div v-if="!loading && !product" class="box p-4 text-center">
            <p class="text-muted">{{ t('settings-products-product_not_found') }}</p>
            <KvButton variant="outlined" to="/settings/products">{{ t('back') }}</KvButton>
        </div>
    </div>
</template>

<script>
import keviApi from "../keviApi";
import KvTabs from "../components/tabs/kv-tabs.vue";
import KvTab from "../components/tabs/kv-tab.vue";
import KvTabPanel from "../components/tabs/kv-tab-panel.vue";
import KvCard from "../components/ui/kv-card.vue";
import KvButton from "../components/form/kv-button.vue";
import KvSkeleton from "../components/ui/kv-skeleton.vue";

const RecommendationList = {
    name: "recommendation-list",
    props: {
        items: { type: Array, default: () => [] },
        removable: { type: Boolean, default: false },
    },
    components: { KvButton },
    template: `
        <div>
            <div v-if="items.length === 0" class="text-muted small">{{ t('no_results_found') }}</div>
            <div v-else class="rec-list">
                <div v-for="item in items" :key="item.id" class="rec-item">
                    <div class="rec-row">
                        <router-link :to="'/settings/products/' + (item.related_product_id || item.id)" class="rec-photo" style="text-decoration: none; color: inherit">
                            <img
                                v-if="getImage(item)"
                                :src="getImage(item)"
                                style="width:48px;height:auto;object-fit:cover;border-radius:6px;border:1px solid #e5e7eb;display:block"
                                alt=""
                            />
                            <span
                                v-else
                                class="fa fa-image"
                                style="width:48px;height:48px;display:inline-flex;align-items:center;justify-content:center;border-radius:6px;background:#f3f4f6;color:#bbb;font-size:14px;border:1px solid #e5e7eb"
                            ></span>
                        </router-link>
                        <div class="rec-info-col">
                            <div class="rec-info-top">
                                <div class="rec-info-main">
                                    <div class="rec-title">{{ item.name }}</div>
                                    <span v-if="item.price" class="badge badge-light rec-price-badge">{{ currencySymbol }} {{ fmtPrice(item.price) }}</span>
                                </div>
                                <div class="d-flex align-items-center rec-actions" style="gap: 6px">
                                    <span v-if="item.score" class="badge badge-light" style="font-size:0.75rem">{{ Math.round(item.score * 100) }}%</span>
                                    <KvButton v-if="removable" size="small" variant="outlined" class="rec-remove-btn" @click="$emit('remove', item.related_product_id || item.id)">{{ t('remove') }}</KvButton>
                                </div>
                            </div>
                            <div v-if="item.description || item.relation_description" class="rec-justification">
                                {{ item.description || item.relation_description }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        getImage(item) {
            const imgs = Array.isArray(item.images) ? item.images : [];
            return imgs.length > 0 ? imgs[0] : null;
        },
        fmtPrice(v) {
            if (!v && v !== 0) return "";
            return parseFloat(v).toFixed(2).replace(".", ",");
        },
    },
};

export default {
    components: { KvTabs, KvTab, KvTabPanel, KvCard, KvButton, KvSkeleton, RecommendationList },
    data: () => ({
        loading: true,
        saving: false,
        product: null,
        selectedImage: null,
        activeTab: "purchases",
        recommendations: { copurchase: [], also_bought: [], ai: [] },
        recsLoading: false,
        aiPrompt: "",
        aiLoading: false,
        relatedProducts: [],
        relatedLoading: false,
        linkSearch: "",
        linkLoading: false,
        linkResults: [],
        linkDescriptions: {},
        manualReasonDrafts: {},
        manualReasonSaving: {},
        manualReasonTimers: {},
    }),
    computed: {
        images() {
            if (this.product && this.product.images && Array.isArray(this.product.images)) return this.product.images;
            return [];
        },
        purchaseHistorySource() {
            const id = this.$route.params.id;
            return `products/${id}/purchases`;
        },
        categoryBadges() {
            const badges = [];
            const category = (this.product && this.product.category) ? String(this.product.category).trim() : "";
            if (category) badges.push(category);

            const groups = (this.product && Array.isArray(this.product.groups)) ? this.product.groups : [];
            groups.forEach((g) => {
                const name = (g && (g.groupName || g.name)) ? String(g.groupName || g.name).trim() : "";
                if (name) badges.push(name);
            });

            // remove duplicates while preserving order
            return badges.filter((b, i) => badges.indexOf(b) === i);
        },
    },
    watch: {
        activeTab(tab) {
            if (tab === "copurchase" && this.recommendations.copurchase.length === 0) this.loadRecs("copurchase");
            if (tab === "also_bought" && this.recommendations.also_bought.length === 0) this.loadRecs("also_bought");
            if (tab === "manual" && this.relatedProducts.length === 0) this.loadRelated();
        },
    },
    mounted() {
        this.loadProduct();
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        loadProduct() {
            const id = this.$route.params.id;
            this.loading = true;
            this.$http(keviApi.getOptions(`/products/detail/${id}`, "GET")).then(
                (response) => {
                    this.loading = false;
                    if (response.status === 200 && response.data.data) {
                        this.product = response.data.data;
                        if (this.images.length > 0) this.selectedImage = this.images[0];
                    }
                },
                () => { this.loading = false; },
            );
        },
        loadRecs(method) {
            const id = this.$route.params.id;
            this.recsLoading = true;
            this.$http(keviApi.getOptions(`/products/${id}/recommendations`, "GET", { method })).then(
                (response) => { this.recsLoading = false; this.$set(this.recommendations, method, response.data.data || []); },
                () => { this.recsLoading = false; },
            );
        },
        loadAiRecs() {
            const id = this.$route.params.id;
            this.aiLoading = true;
            this.$http(keviApi.getOptions(`/products/${id}/recommendations`, "GET", { method: "ai", prompt: this.aiPrompt || null })).then(
                (response) => { this.aiLoading = false; this.$set(this.recommendations, "ai", response.data.data || []); },
                () => { this.aiLoading = false; },
            );
        },
        loadRelated() {
            const id = this.$route.params.id;
            this.relatedLoading = true;
            this.$http(keviApi.getOptions(`/products/${id}/related`, "GET")).then(
                (response) => {
                    this.relatedProducts = response.data.data || [];
                    const drafts = {};
                    (this.relatedProducts || []).forEach((item) => {
                        const rid = item.related_product_id || item.id;
                        drafts[rid] = item.relation_description || "";
                    });
                    this.manualReasonDrafts = drafts;
                    this.relatedLoading = false;
                },
                () => { this.relatedLoading = false; },
            );
        },
        searchProductsToLink() {
            if (!this.linkSearch) return;
            this.linkLoading = true;
            this.$http(keviApi.getOptions("/products/list", "GET", { search: this.linkSearch, quantityPerPage: 10 })).then(
                (response) => {
                    const currentId = this.$route.params.id;
                    const relatedIds = this.relatedProducts.map((r) => r.related_product_id);
                    this.linkResults = (response.data.data || []).filter((p) => p.id !== currentId && !relatedIds.includes(p.id));
                    this.linkLoading = false;
                },
                () => { this.linkLoading = false; },
            );
        },
        addRelated(relatedProductId) {
            const id = this.$route.params.id;
            this.saving = true;
            this.$http(keviApi.getOptions(`/products/${id}/related`, "POST", { relatedProductId, description: this.linkDescriptions[relatedProductId] || null })).then(
                () => { this.saving = false; this.linkSearch = ""; this.linkResults = []; this.linkDescriptions = {}; this.loadRelated(); this.$store.dispatch("notification/success", this.t('settings-products-product_linked')); },
                () => { this.saving = false; this.$store.dispatch("notification/warning", this.t('settings-products-something_went_wrong')); },
            );
        },
        scheduleManualReasonSave(relatedProductId) {
            const id = String(relatedProductId);
            if (this.manualReasonTimers[id]) {
                clearTimeout(this.manualReasonTimers[id]);
            }
            this.$set(this.manualReasonTimers, id, setTimeout(() => {
                this.saveManualReason(id, { silent: true });
            }, 500));
        },
        saveManualReason(relatedProductId, { silent } = { silent: false }) {
            const id = this.$route.params.id;
            const reason = (this.manualReasonDrafts[relatedProductId] || "").trim();
            const item = (this.relatedProducts || []).find((r) => String(r.related_product_id || r.id) === String(relatedProductId));
            const current = (item && item.relation_description) ? String(item.relation_description).trim() : "";

            if (reason === current) {
                if (!silent) this.$store.dispatch("notification/success", this.t('settings-products-recommendation_saved'));
                return;
            }

            this.$set(this.manualReasonSaving, relatedProductId, true);
            this.$http(keviApi.getOptions(`/products/${id}/related/${relatedProductId}`, "DELETE")).then(
                () => {
                    this.$http(keviApi.getOptions(`/products/${id}/related`, "POST", { relatedProductId, description: reason || null })).then(
                        () => {
                            this.$set(this.manualReasonSaving, relatedProductId, false);
                            this.loadRelated();
                            if (!silent) this.$store.dispatch("notification/success", this.t('settings-products-recommendation_saved'));
                        },
                        () => {
                            this.$set(this.manualReasonSaving, relatedProductId, false);
                            this.$store.dispatch("notification/warning", this.t('settings-products-something_went_wrong'));
                        },
                    );
                },
                () => {
                    this.$set(this.manualReasonSaving, relatedProductId, false);
                    this.$store.dispatch("notification/warning", this.t('settings-products-something_went_wrong'));
                },
            );
        },
        removeRelated(relatedProductId) {
            const id = this.$route.params.id;
            this.saving = true;
            this.$http(keviApi.getOptions(`/products/${id}/related/${relatedProductId}`, "DELETE")).then(
                () => { this.saving = false; this.loadRelated(); this.$store.dispatch("notification/success", this.t('settings-products-link_removed')); },
                () => { this.saving = false; this.$store.dispatch("notification/warning", this.t('settings-products-something_went_wrong')); },
            );
        },
        firstImage(item) {
            if (item.images && Array.isArray(item.images) && item.images.length > 0) return item.images[0];
            return null;
        },
        formatPrice(value) {
            if (!value && value !== 0) return "";
            return parseFloat(value).toFixed(2).replace(".", ",");
        },
        formatDate(dateStr) {
            if (!dateStr) return "";
            return new Date(dateStr).toLocaleDateString("pt-BR");
        },
        statusLabel(status) {
            return { active: this.t('active'), out_of_stock: this.t('unavailable'), paused: this.t('paused'), archived: this.t('archived') }[status] || status || "—";
        },
        statusBadgeClass(status) {
            return { active: "badge-success", out_of_stock: "badge-warning", paused: "badge-secondary", archived: "badge-dark" }[status] || "badge-light";
        },
    },
};
</script>

<style scoped>
.product-hero-card {
    background: #fff;
}
.product-content-card {
    background: #fff;
}
.product-title { font-size: 1.5rem; }
.product-description { line-height: 1.35; }
.product-badges { display: flex; flex-wrap: wrap; gap: 6px; }
.product-category-badge {
    background: #f3f4f6;
    color: #374151;
    border: 1px solid #e5e7eb;
    font-size: 0.7rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    padding: 6px 8px;
    border-radius: 999px;
    text-transform: none;
}

.fw-500 { font-weight: 500; }
.stats-insights { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
.stat-title { color: #6C737F; font-size: 0.75rem; font-weight: 500; }
.stat-value { font-size: 1.15rem; font-weight: 500; color: #111827; line-height: 1.2; }
.stat-insight-white { background: #fff !important; }
@media (max-width: 1200px) { .stats-insights { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 768px) { .stats-insights { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 420px) { .stats-insights { grid-template-columns: 1fr; } }
.gallery-main { border-radius: 10px; overflow: hidden; background: #f9fafb; border: 1px solid #e5e7eb; }
.gallery-main-img { width: 100%; max-height: 420px; object-fit: contain; display: block; }
.gallery-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 260px; color: #ccc; font-size: 48px; }
.gallery-thumbs { display: flex; gap: 8px; flex-wrap: wrap; }
.gallery-thumb { width: 64px; height: 64px; object-fit: cover; border-radius: 8px; border: 2px solid transparent; cursor: pointer; transition: border-color 0.2s; }
.gallery-thumb:hover { border-color: #aaa; }
.gallery-thumb.active { border-color: #2aaa4c; }
.product-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 24px; }
.info-item label { display: block; font-size: 0.75rem; text-transform: uppercase; color: #888; margin-bottom: 2px; font-weight: 500; letter-spacing: 0.5px; }
.info-item span { font-size: 0.95rem; color: #333; }

.kv-field { position: relative; }
.kv-field-input {
    height: 44px;
    padding: 10px 112px 10px 12px;
    border-radius: 12px;
    border: 1px solid #E5E7EB;
    background: #FBFBFB;
    color: #111827;
}
.kv-field-input::placeholder { color: #9CA3AF; }
.kv-field-btn {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    height: 34px;
}
.kv-field-ai .kv-field-input { padding-right: 110px; }
.kv-field-manual .kv-field-input { padding-right: 98px; }

.ai-loading .rec-item { border: none; border-bottom: 1px solid #e5e7eb; background: transparent; }
.ai-loading .rec-item:last-child { border-bottom: none; }
.ai-thumb-skeleton { width: 36px; height: 36px; border-radius: 6px; background: #f0f2f5; border: 1px solid #e5e7eb; }

.rec-link { gap: 10px; text-decoration: none; color: inherit; min-width: 0; }
.rec-thumb { width: 48px; height: auto; object-fit: cover; border-radius: 6px; border: 1px solid #e5e7eb; display: block; }
.rec-thumb-placeholder { width: 48px; display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; background: #f3f4f6; color: #bbb; font-size: 14px; border: 1px solid #e5e7eb; }
.rec-meta { flex: 1; min-width: 0; }

.rec-justification-input {
    margin-top: 0px;
    font-family: "Inter", "Montserrat", sans-serif;
    font-size: 12px;
    color: #111827;
    background: #FBFBFB;
    border: 1px solid #E5E7EB;
    border-radius: 10px;
    height: 34px;
    padding: 6px 10px;
    width: 100%;
}
.rec-justification-input::placeholder { color: #9CA3AF; }

.link-results { border: none; border-radius: 0; max-height: 240px; overflow-y: auto; }
.link-result-item { padding: 12px 0; border-bottom: 1px solid #e5e7eb; }
.link-result-item:last-child { border-bottom: none; }
.link-thumb { width: 36px; height: 36px; object-fit: cover; border-radius: 6px; border: 1px solid #e5e7eb; }
.link-thumb-placeholder { width: 36px; height: 36px; display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; background: #f3f4f6; color: #bbb; font-size: 14px; }
.rec-title {
    font-family: "Sora", "Montserrat", sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 1.2;
    color: #111827;
}
.rec-justification {
    font-family: "Inter", "Montserrat", sans-serif;
    font-size: 12px;
    line-height: 1.3;
    color: #6B7280;
    margin-top: 4px;
}
@media (max-width: 576px) { .product-info-grid { grid-template-columns: 1fr; } }
</style>

<style>
.rec-list { display: flex; flex-direction: column; gap: 0; }
.rec-item { padding: 12px 8px; margin: 0 -8px; border-radius: 0; border: none; border-bottom: 1px solid #e5e7eb; transition: background 0.15s; }
.rec-item:last-child { border-bottom: none; }
.rec-item:hover { background: #fafafa; }

/* NOTE: these are NOT scoped because RecommendationList
   is an inline child component inside this SFC. */
.rec-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}
.rec-photo {
    flex-shrink: 0;
    width: 48px;
}
.rec-photo .rec-thumb,
.rec-photo img {
    width: 48px;
    height: auto;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    display: block;
}
.rec-photo .rec-thumb-placeholder {
    width: 48px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background: #f3f4f6;
    color: #bbb;
    font-size: 14px;
    border: 1px solid #e5e7eb;
}
.rec-info-col {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.rec-info-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}
.rec-info-main {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}
.rec-link {
    flex: 1;
}
.rec-info { flex: 1; min-width: 0; }
.rec-actions { align-self: flex-start; }
.rec-remove-btn.kv-button {
    height: 26px;
    padding: 4px 8px;
    font-size: 12px;
}
.rec-title {
    font-family: "Sora", "Montserrat", sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 1.2;
    color: #111827;
}
.rec-justification {
    font-family: "Inter", "Montserrat", sans-serif;
    font-size: 12px;
    line-height: 1.3;
    color: #6B7280;
    margin-top: 4px;
}
.rec-price-badge {
    font-size: 0.7rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    padding: 6px 8px;
    border-radius: 999px;
}
</style>
